import { DailySentence, ArchiveEntry, GameSentence, LLMResponse } from '../types';
import { supabase } from './supabaseClient';

export class DatabaseService {
  // User Management
  static async signUp(email: string, password: string, userData: { name: string; grade: string; difficulty: string }) {
    if (!supabase) {
      throw new Error('Supabase not configured');
    }
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name: userData.name,
          grade: userData.grade,
          difficulty: userData.difficulty
        }
      }
    });

    if (error) throw error;

    // Create user profile
    if (data.user) {
      await this.createUserProfile(data.user.id, userData);
    }

    return data;
  }

  static async signIn(email: string, password: string) {
    if (!supabase) {
      throw new Error('Supabase not configured');
    }
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) throw error;
    return data;
  }

  static async signOut() {
    if (!supabase) {
      throw new Error('Supabase not configured');
    }
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }

  static async getCurrentUser() {
    if (!supabase) {
      throw new Error('Supabase not configured');
    }
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  }

  static async getCurrentUserId(): Promise<string | null> {
    if (!supabase) {
      return null;
    }
    const { data: { session } } = await supabase.auth.getSession();
    return session?.user?.id ?? null;
  }

  static async createUserProfile(userId: string, userData: { name: string; grade: string; difficulty: string }) {
    if (!supabase) {
      throw new Error('Supabase not configured');
    }
    // Use upsert to handle both new profiles and updates
    const { error } = await supabase
      .from('user_profiles')
      .upsert({
        id: userId,
        name: userData.name,
        grade: userData.grade,
        difficulty: userData.difficulty,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'id'
      });

    if (error) throw error;
  }

  static async getUserProfile(userId: string) {
    if (!supabase) {
      throw new Error('Supabase not configured');
    }
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (error) throw error;
    return data;
  }

  static async updateUserStats(userId: string, score: number) {
    if (!supabase) {
      throw new Error('Supabase not configured');
    }
    // First get current stats
    const { data: currentStats, error: fetchError } = await supabase
      .from('user_profiles')
      .select('total_score, games_played')
      .eq('id', userId)
      .maybeSingle();

    if (fetchError) throw fetchError;

    // Then update with new values
    const { error } = await supabase
      .from('user_profiles')
      .update({
        total_score: (currentStats?.total_score || 0) + score,
        games_played: (currentStats?.games_played || 0) + 1,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);

    if (error) throw error;
  }

  // Daily Sentences
  static async getDailySentence(date: string, grade: string): Promise<DailySentence | null> {
    if (!supabase) {
      throw new Error('Supabase not configured');
    }
    const { data, error } = await supabase
      .from('daily_sentences')
      .select('*')
      .eq('id', `${date}-${grade}`)
      .maybeSingle();

    if (error) throw error;
    
    if (!data) return null;
    
    // Transform database fields to match TypeScript interface
    return {
      id: data.id,
      date: data.date,
      grade: data.grade,
      topic: data.topic,
      incorrectSentence: data.incorrect_sentence,
      correctSentence: data.correct_sentence,
      errors: data.errors,
      difficulty: data.difficulty,
      isDaily: true
    };
  }

  static async createDailySentence(dailySentence: DailySentence) {
    if (!supabase) {
      throw new Error('Supabase not configured');
    }
    // Upsert so the same date+grade is only stored once (first writer wins; others no-op)
    const { error } = await supabase
      .from('daily_sentences')
      .upsert(
        {
          id: dailySentence.id,
          date: dailySentence.date,
          grade: dailySentence.grade,
          topic: dailySentence.topic,
          incorrect_sentence: dailySentence.incorrectSentence,
          correct_sentence: dailySentence.correctSentence,
          errors: dailySentence.errors,
          difficulty: dailySentence.difficulty
        },
        { onConflict: 'id' }
      );

    if (error) throw error;
  }

  static async saveDailyResult(
    userId: string,
    dailySentenceId: string,
    score: number,
    attempts: number,
    userInput: string,
    corrections: any[]
  ) {
    if (!supabase) {
      throw new Error('Supabase not configured');
    }

    const existing = await this.getUserDailyResult(userId, dailySentenceId);

    const { error } = await supabase
      .from('user_daily_results')
      .upsert({
        user_id: userId,
        daily_sentence_id: dailySentenceId,
        score,
        attempts,
        user_input: userInput,
        corrections
      }, {
        onConflict: 'user_id,daily_sentence_id'
      });

    if (error) throw error;

    if (!existing) {
      await this.updateUserStats(userId, score);
    }
  }

  static async getUserDailyResult(userId: string, dailySentenceId: string): Promise<ArchiveEntry | null> {
    if (!supabase) {
      return null;
    }

    const { data, error } = await supabase
      .from('user_daily_results')
      .select(`
        score,
        attempts,
        user_input,
        corrections,
        daily_sentences (
          date,
          grade,
          topic,
          incorrect_sentence,
          correct_sentence
        )
      `)
      .eq('user_id', userId)
      .eq('daily_sentence_id', dailySentenceId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    return this.mapDailyResultRow(data, dailySentenceId);
  }

  static async getUserDailyResults(userId: string, grade: string): Promise<ArchiveEntry[]> {
    if (!supabase) {
      throw new Error('Supabase not configured');
    }
    const { data, error } = await supabase
      .from('user_daily_results')
      .select(`
        score,
        attempts,
        user_input,
        corrections,
        daily_sentence_id,
        daily_sentences (
          date,
          grade,
          topic,
          incorrect_sentence,
          correct_sentence
        )
      `)
      .eq('user_id', userId)
      .order('completed_at', { ascending: false });

    if (error) throw error;
    if (!data) return [];

    return data
      .map(result => this.mapDailyResultRow(result, result.daily_sentence_id))
      .filter((entry): entry is ArchiveEntry => entry !== null && entry.grade === grade);
  }

  static async getUserDailyResultsForDate(userId: string, date: string): Promise<ArchiveEntry[]> {
    if (!supabase) {
      return [];
    }

    const { data, error } = await supabase
      .from('user_daily_results')
      .select(`
        score,
        attempts,
        user_input,
        corrections,
        daily_sentence_id,
        daily_sentences (
          date,
          grade,
          topic,
          incorrect_sentence,
          correct_sentence
        )
      `)
      .eq('user_id', userId)
      .like('daily_sentence_id', `${date}-%`);

    if (error) throw error;
    if (!data) return [];

    return data
      .map(result => this.mapDailyResultRow(result, result.daily_sentence_id))
      .filter((entry): entry is ArchiveEntry => entry !== null && entry.date === date);
  }

  private static mapDailyResultRow(result: any, dailySentenceId: string): ArchiveEntry | null {
    const sentence = Array.isArray(result.daily_sentences)
      ? result.daily_sentences[0]
      : result.daily_sentences;

    if (sentence) {
      return {
        date: sentence.date,
        grade: sentence.grade,
        topic: sentence.topic,
        incorrectSentence: sentence.incorrect_sentence,
        correctSentence: sentence.correct_sentence,
        userScore: result.score,
        userAttempts: result.attempts,
        userInput: result.user_input,
        corrections: result.corrections
      };
    }

    const match = String(dailySentenceId || '').match(/^(\d{4}-\d{2}-\d{2})-(.+)$/);
    if (!match) {
      return null;
    }

    return {
      date: match[1],
      grade: match[2],
      topic: 'Daily Challenge',
      incorrectSentence: '',
      correctSentence: '',
      userScore: result.score,
      userAttempts: result.attempts,
      userInput: result.user_input,
      corrections: result.corrections
    };
  }

  // Sentence Caching
  static async getCachedSentence(topic: string, grade: string, difficulty: string): Promise<GameSentence | null> {
    if (!supabase) {
      throw new Error('Supabase not configured');
    }
    const { data, error } = await supabase
      .from('sentence_cache')
      .select('*')
      .eq('topic', topic)
      .eq('grade', grade)
      .eq('difficulty', difficulty)
      .order('usage_count', { ascending: true })
      .limit(1);

    if (error) throw error;
    
    if (data && data.length > 0) {
      const cachedSentence = data[0];
      // Update usage count
      await this.updateSentenceUsage(cachedSentence.id);
      
      return {
        id: cachedSentence.id,
        incorrectSentence: cachedSentence.incorrect_sentence,
        correctSentence: cachedSentence.correct_sentence,
        topic: cachedSentence.topic,
        difficulty: cachedSentence.difficulty as 'easy' | 'medium' | 'hard',
        errors: cachedSentence.errors
      };
    }

    return null;
  }

  static async cacheSentence(llmResponse: LLMResponse, topic: string, grade: string, difficulty: string) {
    if (!supabase) {
      throw new Error('Supabase not configured');
    }
    const { error } = await supabase
      .from('sentence_cache')
      .insert({
        topic,
        grade,
        difficulty,
        incorrect_sentence: llmResponse.incorrectSentence,
        correct_sentence: llmResponse.correctSentence,
        errors: llmResponse.errors
      });

    if (error) throw error;
  }

  static async updateSentenceUsage(sentenceId: string) {
    if (!supabase) {
      throw new Error('Supabase not configured');
    }
    // First get current usage count
    const { data: currentUsage, error: fetchError } = await supabase
      .from('sentence_cache')
      .select('usage_count')
      .eq('id', sentenceId)
      .maybeSingle();

    if (fetchError) throw fetchError;

    // Then update with new count
    const { error } = await supabase
      .from('sentence_cache')
      .update({
        usage_count: (currentUsage?.usage_count || 0) + 1,
        last_used_at: new Date().toISOString()
      })
      .eq('id', sentenceId);

    if (error) throw error;
  }

  // Analytics and Reporting
  static async getUserStats(userId: string) {
    if (!supabase) {
      throw new Error('Supabase not configured');
    }
    const { data, error } = await supabase
      .from('user_profiles')
      .select('total_score, games_played')
      .eq('id', userId)
      .maybeSingle();

    if (error) throw error;
    return data;
  }

  static async getLeaderboard(grade: string, limit: number = 10) {
    if (!supabase) {
      throw new Error('Supabase not configured');
    }
    const { data, error } = await supabase
      .from('user_profiles')
      .select('name, total_score, games_played')
      .eq('grade', grade)
      .order('total_score', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data;
  }

  // Cleanup and Maintenance
  static async cleanupOldCache(daysOld: number = 30) {
    if (!supabase) {
      throw new Error('Supabase not configured');
    }
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);

    const { error } = await supabase
      .from('sentence_cache')
      .delete()
      .lt('last_used_at', cutoffDate.toISOString());

    if (error) throw error;
  }
}

// Real-time subscriptions for live updates
export const subscribeToUserResults = (userId: string, callback: (payload: any) => void) => {
  if (!supabase) {
    throw new Error('Supabase not configured');
  }
  return supabase
    .channel('user_results')
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'user_daily_results',
      filter: `user_id=eq.${userId}`
    }, callback)
    .subscribe();
}; 